import { useState, useEffect } from "react";
import { Outlet, NavLink, useParams, useLocation } from "react-router-dom";
import {
  Trophy,
  Users,
  Calendar,
  BarChart3,
  Shield,
  User,
  Menu,
  X,
  Target,
  Home,
  FileText,
  MessageSquare,
} from "lucide-react";
import { Button } from "./ui/button";
import { getPublicData, recordVisit } from "../lib/api";
import { ClipboardList, Building2 } from "lucide-react";

const navItems = [
  { to: "dashboard", icon: Home, label: "Dashboard" },
  { to: "standings", icon: BarChart3, label: "Standings" },
  { to: "matches", icon: Calendar, label: "Matches" },
  { to: "match-officials", icon: ClipboardList, label: "Match Officials" },
  { to: "knockout", icon: Trophy, label: "Knockout" },
  { to: "teams", icon: Shield, label: "Teams" },
  { to: "players", icon: Users, label: "Players" },
  { to: "player-stats", icon: User, label: "Player Stats" },
  { to: "team-stats", icon: Target, label: "Team Stats" },
  { to: "sponsors", icon: Building2, label: "Sponsors" },
  { to: "blog", icon: FileText, label: "Blog" },
  { to: "contact", icon: MessageSquare, label: "Contact" },
];

export default function PublicLayout() {
  const { token } = useParams();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settings, setSettings] = useState({ name: "Tournament", logo: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getPublicData(token);
        if (res.data?.settings) {
          setSettings(res.data.settings);
          setError(null);
        } else {
          setError("Invalid tournament link. Please check the URL and try again.");
        }
      } catch (error) {
        console.error("Failed to fetch settings:", error);
        if (error.response?.status === 404) {
          setError("Tournament not found. Please check the URL.");
        } else {
          setError("Unable to load tournament data. The server may be temporarily unavailable. Please try again in a moment.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [token]);

  // Track page visits
  useEffect(() => {
    const trackVisit = async () => {
      try {
        await recordVisit({
          page: location.pathname,
          user_agent: navigator.userAgent,
          referrer: document.referrer || null
        });
      } catch (error) {
        // Silently fail - don't disrupt user experience
      }
    };
    trackVisit();
  }, [location.pathname]);

  // Show error state instead of redirecting to login
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h1 className="text-xl font-bold mb-2">Connection Error</h1>
          <p className="text-muted-foreground mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const NavItem = ({ to, icon: Icon, label }) => (
    <NavLink
      to={`/public/${token}/${to}`}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
          isActive
            ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25"
            : "text-muted-foreground hover:bg-secondary hover:text-foreground"
        }`
      }
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium">{label}</span>
    </NavLink>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-lg border-b border-border h-16 flex items-center px-4">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 hover:bg-secondary rounded-lg transition-colors"
          data-testid="public-mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-3 ml-3">
          {settings.logo ? (
            <img
              src={settings.logo}
              alt="Logo"
              className="w-8 h-8 rounded-lg object-cover"
            />
          ) : (
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Trophy className="w-4 h-4 text-primary" />
            </div>
          )}
          <div>
            <h1 className="font-heading text-sm font-bold uppercase tracking-wider">
              {settings.name}
            </h1>
            <p className="text-xs text-muted-foreground">Public View</p>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-72 bg-card border-r border-border z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Sidebar Header */}
        <div className="h-20 flex items-center justify-between px-6 border-b border-border">
          <div className="flex items-center gap-3">
            {settings.logo ? (
              <img
                src={settings.logo}
                alt="Logo"
                className="w-12 h-12 rounded-xl object-cover border border-primary/30"
              />
            ) : (
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center border border-primary/30">
                <Trophy className="w-6 h-6 text-primary" />
              </div>
            )}
            <div>
              <h1 className="font-heading text-base font-bold uppercase tracking-wider leading-tight">
                {settings.name?.split(' ').slice(0, 2).join(' ')}
              </h1>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">
                {settings.name?.split(' ').slice(2).join(' ') || 'Tournament'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 hover:bg-secondary rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-5rem)]">
          {navItems.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen pt-16 lg:pt-0">
        <div className="p-4 md:p-8">
          <Outlet context={{ token, settings }} />
        </div>
      </main>
    </div>
  );
}
